import { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Copy, Globe, Link2, Loader2 } from 'lucide-react';
import { runBtnClass, stopBtnSmClass } from './controls';
import { DemoLock } from './DemoLock';
import { IS_DEMO } from '../../apiBase';
import { useLanguage } from '../../contexts/LanguageContext';
import { QrCode } from './QrCode';
import { isStandalone, replaceIfStandalone } from '../../standalone';
import { currentRun } from './docState';
import { publishTutorial, unpublishTutorial, type PublishSnapshot } from './publishApi';
import { refreshPublishStatus, usePublishStatus } from './publishStatus';

// "Publish" control (Phase 5 tab): pushes a metadata snapshot to the backend so this
// manual becomes a public, no-login page, then shows the public link + a print-ready QR.
// Publishing is blocked (mirrored from the backend) until the pipeline has made the thing
// that pipeline publishes. Unpublish is soft, so re-publishing later reactivates the same
// printed QR.
export type PublishBlock = 'loading' | 'needModel' | 'needClip' | null;

export function PublishPanel({
  snapshot,
  active,
  blocked,
}: {
  snapshot: PublishSnapshot;
  active: boolean;
  // WHY PUBLISH AND UPDATE ARE DEAD, or null when they are not — derived by the parent from the
  // stores it already reads for the review, so nothing is fetched twice. The parent owns the
  // rule; this panel only draws the answer. `loading`: the clips are still being worked out,
  // and a snapshot sent now would be one with no screens in it, which the server refuses
  // (Louis 2026-10-23: an Update pressed two seconds after opening the page was).
  blocked: PublishBlock;
}) {
  const { t } = useLanguage();
  const docId = snapshot.docId;
  const blockedHint = blocked && blocked !== 'loading' ? t(`notice.publish.${blocked}`) : null;
  const canPublish = blocked == null;

  // Publish state lives in a shared store: the phase-progress bar reads the same
  // `published` + `updatedAt`, so clicking publish here moves the bar at once.
  const { published, loaded, url: liveUrl } = usePublishStatus(docId);
  // THE ADDRESS THE SERVER MINTED: the slug is built at publish time, and only the server knows
  // it. Drawn only once published, when there is one.
  const url = liveUrl ?? '';
  const loading = !loaded;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Transient "publication succeeded" confirmation, shown briefly after a
  // (re)publish so a successful update — which otherwise changes nothing visible —
  // gives clear feedback.
  const [justPublished, setJustPublished] = useState(false);
  useEffect(() => {
    if (!justPublished) return;
    const timer = setTimeout(() => setJustPublished(false), 2500);
    return () => clearTimeout(timer);
  }, [justPublished]);

  // Refetch the publish state whenever this tab becomes visible (the tab sections
  // stay mounted, so a mount-only fetch could go stale after a re-publish).
  useEffect(() => {
    if (active) void refreshPublishStatus(docId);
  }, [docId, active]);

  const doPublish = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setJustPublished(false);
    try {
      await publishTutorial(snapshot, currentRun(docId));
      // Re-read rather than assume: the bar needs the server's publish timestamp to
      // tell the page apart from a stale one.
      await refreshPublishStatus(docId);
      setJustPublished(true);
    } catch (e) {
      setError(
        (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
          t('notice.publish.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  const doUnpublish = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setJustPublished(false);
    try {
      await unpublishTutorial(docId);
      await refreshPublishStatus(docId);
    } catch {
      setError(t('notice.publish.error'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked; the link is still selectable */
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-center text-sm font-bold uppercase tracking-wide text-slate-600">
        {t('notice.publish.title')}
      </div>
      {/* Always render the section (never a blank), so a slow/failed status
          fetch can't hide the control. While loading we only defer the
          published-vs-not branch. */}
      {loading ? (
        <div className="flex justify-center py-2 text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : !published ? (
        <div className="flex flex-col gap-2">
          <DemoLock>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={doPublish}
              disabled={busy || !canPublish}
              className={runBtnClass}
              title={blockedHint ?? undefined}
            >
              {busy || blocked === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
              {t('notice.publish.publish')}
            </button>
          </div>
          </DemoLock>
          {blockedHint && (
            <p className="text-center text-xs text-slate-400">
              {blockedHint}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Re-publish (refresh the snapshot) + soft unpublish. GONE in the demo
              rather than greyed out, which is the difference between the two states
              this panel has. Unpublished, the dead Publish button is the phase saying
              what it does and that this copy will not do it. Published, the page is
              already there and its link is below — Update and Unpublish are then two
              controls for a page nobody visiting can change, offering a decision that
              is not on the table.

              FIRST, over the link and the QR — where Publish stands before there is a page:
              a run goes right under its heading, whichever state the panel is in.

              THE SANDBOX KEEPS THEM, GREYED. It is not being shown to anybody: it is
              prod's app with the spending switched off, and what somebody working in it
              needs to see is the app as it really is, with the parts that cost money
              plainly dead. Hiding controls there would be the copy differing from prod
              in a way nobody asked it to. */}
          {!IS_DEMO && (
          <DemoLock>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button type="button" onClick={doPublish} disabled={busy || !canPublish} title={blockedHint ?? undefined} className={runBtnClass}>
              {(busy || blocked === 'loading') && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('notice.publish.republish')}
            </button>
            {/* Secondary next to "update": the destructive action shouldn't
                compete with the one users take every time. */}
            <button
              type="button"
              onClick={doUnpublish}
              disabled={busy}
              className={stopBtnSmClass}
            >
              {t('notice.publish.unpublish')}
            </button>
          </div>
          </DemoLock>
          )}

          {/* Public link */}
          <div className="flex flex-col gap-1">
            {/* CENTRED ON THE PANEL, with the icon and the copy button riding beside the
                address rather than pinned to the two ends of the row. The link used to be
                told to fill the width, which put it hard against the left margin under a
                centred heading and a centred QR code — the one line in the block that was
                not on the panel's own axis. It still truncates: a slug is long, and the
                narrow case has to fall back to an ellipsis rather than a second line. */}
            <div className="flex items-center justify-center gap-2">
              <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
              {/* A NEW TAB, except in the installed app, which has none: there it takes the app's
                  place, the app's page let go of (see replaceIfStandalone). */}
              <a
                href={url}
                target={isStandalone() ? undefined : '_blank'}
                onClick={(e) => replaceIfStandalone(e, url)}
                rel="noreferrer"
                className="min-w-0 truncate text-sm text-blue-600 underline"
              >
                {url}
              </a>
              <button
                type="button"
                onClick={copy}
                aria-label={t('notice.publish.copy')}
                title={copied ? t('notice.publish.copied') : t('notice.publish.copy')}
                className="shrink-0 rounded-md border border-slate-300 p-1.5 text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-500"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Print-ready QR */}
          <QrCode url={url} downloadName={`noticeia-${docId}`} />

        </div>
      )}

      {error && (
        <p className="text-center text-sm text-rose-600">{error}</p>
      )}
      {justPublished && !error && (
        <p className="flex items-center justify-center gap-1.5 text-center text-sm font-medium text-blue-600">
          <Check className="h-4 w-4" />
          {t('notice.publish.success')}
        </p>
      )}
    </div>
  );
}
